import { useEffect, useMemo, useState } from 'react';
import { TYPE_COLOR_MAP, type CalendarEventType } from '../../../../../api/calendar';
import { getDateLocale } from '../../../../../lib/base-i18n';
import { useMenuGate } from '../../../../../lib/menu-gate';
import { useAuthStore } from '../../../../../store/authStore';
import { WidgetCard } from '../../WidgetCard';
import { useWidgetActions } from '../../widget-context';
import { useWidgetItems } from '../../use-widget-items';
import { useMonthEvents } from '../../widget-queries';
import {
  addDays, inRange, mmdd, startOfDay, touchesMonth, uniqueById, weekStart, type Range,
} from './period';
import './widgets-v2.css';

/**
 * 일정 상세 v2 — 보이는 탭 · 기본 탭 · 기간 · 개수 · 줄에 보일 것 (2026-09-16 · 대시보드 시안 v2 V3a · 깃발 켤 때만).
 *
 * ★★★ 탭 칩(회사 주요일정 · 내 일정 · 팀원 일정)은 **이미 있던 것**이다(W-Q3). 설정의 「기본 탭」은 처음 값이고
 *     탭을 눌러 바꾼 것은 그 자리에서만 산다(규칙 D4). 켜 둔 탭이 하나면 칩 줄을 세우지 않는다.
 * ★★ 탭의 뜻은 옛 위젯 그대로다 — 회사 주요일정 = 교육 · 기념일 · 내 일정 = 내가 만든 것 · 팀원 일정 = 남이 만든 것
 *    (서버에 회사 / 팀원 구분 칸이 없다).
 * ★ 기간이 옆 달에 걸치면 그 달 일정만 더 받는다. 하루가 아닌 기간은 시각 앞에 날짜를 붙인다.
 */

type ScopeKey = 'company' | 'mine' | 'team';
const SCOPE_LABEL: Record<ScopeKey, string> = { company: '회사 주요일정', mine: '내 일정', team: '팀원 일정' };
const EMPTY_TEXT: Record<string, string> = {
  today: '오늘 일정이 없습니다', today2: '오늘 · 내일 일정이 없습니다', week: '이번 주 일정이 없습니다',
};

function typeMeta(type: string) {
  return TYPE_COLOR_MAP[type as CalendarEventType] ?? { label: '기타', color: 'var(--text-muted)' };
}

export function ScheduleDetailWidgetV2() {
  const { go } = useWidgetActions();
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const gate = useMenuGate('calendar', '캘린더');
  const items = useWidgetItems('schedule');

  const tabs = items.visible('tabs') as ScopeKey[];
  const defaultTab = items.option<string>('defaultTab') as ScopeKey | undefined;
  const initialTab: ScopeKey = defaultTab && tabs.includes(defaultTab) ? defaultTab : (tabs[0] ?? 'mine');
  const [scope, setScope] = useState<ScopeKey>(initialTab);
  useEffect(() => { setScope(initialTab); }, [initialTab]);

  const count = Number(items.option<string>('count')) || 5;
  const period = items.option<string>('period') ?? 'today';
  const showLocation = items.has('location');
  const showAttendees = items.has('attendees');
  const showColor = items.has('typeColor');

  const today = startOfDay(new Date());
  const range: Range = period === 'week'
    ? { from: weekStart(today), to: addDays(weekStart(today), 7) }
    : { from: today, to: addDays(today, period === 'today2' ? 2 : 1) };
  const y = today.getFullYear();
  const m = today.getMonth();
  const pm = new Date(y, m - 1, 1);
  const nm = new Date(y, m + 1, 1);
  const cur = useMonthEvents(y, m, gate.enabled);
  const prev = useMonthEvents(pm.getFullYear(), pm.getMonth(), gate.enabled && touchesMonth(range, pm.getFullYear(), pm.getMonth()));
  const next = useMonthEvents(nm.getFullYear(), nm.getMonth(), gate.enabled && touchesMonth(range, nm.getFullYear(), nm.getMonth()));

  const events = useMemo(() => uniqueById([...(cur.data ?? []), ...(prev.data ?? []), ...(next.data ?? [])])
    .filter((e) => inRange(e.startAt, range))
    .filter((e) => {
      if (scope === 'company') return e.type === 'training' || e.type === 'anniversary';
      if (scope === 'mine') return e.creatorId === myId;
      return e.creatorId !== myId;
    })
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
  // range 는 period 로만 정해진다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [cur.data, prev.data, next.data, scope, myId, period]);

  const singleDay = period === 'today';
  const tail = singleDay ? mmdd(today) : `${mmdd(range.from)} – ${mmdd(addDays(range.to, -1))}`;

  return (
    <WidgetCard
      title="일정 상세"
      tail={tail}
      loading={cur.loading || prev.loading || next.loading}
      error={cur.error ?? prev.error ?? next.error}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      {tabs.length > 1 && (
        <div className="dw-seg">
          {tabs.map((k) => (
            <button
              key={k}
              type="button"
              className={`dw-seg__btn${scope === k ? ' is-active' : ''}`}
              aria-pressed={scope === k}
              onClick={() => setScope(k)}
            >
              {SCOPE_LABEL[k]}
            </button>
          ))}
        </div>
      )}
      {events.length === 0
        ? <div className="dw-card__state">{EMPTY_TEXT[period] ?? EMPTY_TEXT.today}</div>
        : (
          <div className="dw-rows">
            {events.slice(0, count).map((e) => {
              const meta = typeMeta(e.type);
              const at = new Date(e.startAt);
              const time = e.isAllDay
                ? '종일'
                : at.toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit', hour12: false });
              const attendees = e.attendees?.length ?? 0;
              const sub = [
                meta.label,
                showLocation ? e.location : null,
                showAttendees && attendees > 0 ? `참석 ${attendees}명` : null,
              ].filter(Boolean).join(' · ');
              return (
                <button type="button" key={e.id} className="dw-ev" onClick={() => go('/calendar')}>
                  <span className="dw-ev__time">{singleDay ? time : `${mmdd(at)} ${time}`}</span>
                  <span
                    className="dw-ev__bar"
                    style={{ background: showColor ? meta.color : 'var(--border-strong)' }}
                    aria-hidden
                  />
                  <span className="dw-ev__main">
                    <span className="dw-ev__t" title={e.title}>{e.title}</span>
                    <span className="dw-ev__s">{sub}</span>
                  </span>
                </button>
              );
            })}
            {events.length > count && <div className="dw-more">+{events.length - count}건 더</div>}
          </div>
        )}
    </WidgetCard>
  );
}
